import { validateRetailer } from "../config/retailers";
import { PUBLIC_DEALS_RETAILERS, isHomeFeedPath } from "../config/publicDeals";

const FREE_LOCKED_GLOBAL_PREFIXES = [
  "/hawk-ai",
  "/hot-deals",
  "/featured-deals",
  "/extension",
  "/home_depot/penny-items-pro",
  "/home_depot/penny-items-beta",
] as const;

const FREE_LOCKED_RETAILER_ROUTES = new Set([
  "tracking",
  "bot-filters",
  "leads",
  "coupons",
  "product-search",
]);

const AUTHENTICATED_GLOBAL_PATHS = new Set([
  "/verify",
  "/onboarding",
  "/success",
  "/setup",
  "/setup-legacy",
  "/settings",
  "/delete-account",
  "/help",
  "/hot-deals",
  "/featured-deals",
  "/hawk-finds",
  "/community-successes",
  "/hawk-ai",
  "/admin/users",
  "/home_depot/penny-items-pro",
  "/home_depot/penny-items-beta",
  "/extension",
  "/online/woot",
  "/product-search",
  "/product",
  "/favorites",
  "/tracking",
  "/bot-filters",
]);

const AUTHENTICATED_RETAILER_ROUTES = new Set([
  "products",
  "product-search",
  "product",
  "tracking",
  "bot-filters",
  "leads",
  "coupons",
]);

/** Account routes that require a paid entitlement even when navigated directly. */
export function isPaidOnlyPathForSignedFree(pathname: string): boolean {
  if (FREE_LOCKED_GLOBAL_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }
  const segments = pathname.split("/").filter(Boolean);
  return segments.length >= 2 && FREE_LOCKED_RETAILER_ROUTES.has(segments[1]);
}

/** Known app routes that cannot render without a valid DealHawk session. */
export function isAuthenticationRequiredPath(pathname: string): boolean {
  if (AUTHENTICATED_GLOBAL_PATHS.has(pathname)) return true;
  if (
    pathname.startsWith("/walmart_online/") ||
    pathname.startsWith("/amazon_online/") ||
    pathname.startsWith("/kohls_online/")
  ) {
    return true;
  }

  const segments = pathname.split("/").filter(Boolean);
  return (
    segments.length >= 2 &&
    validateRetailer(segments[0]) &&
    AUTHENTICATED_RETAILER_ROUTES.has(segments[1])
  );
}

// ---- Signed-in free accounts: the logged-out deals site plus their account pages ----

/** Members-app sections under a store ("/walmart/products"); never a public feed category. */
const MEMBER_STORE_SECTIONS = new Set([
  "products",
  "product",
  "product-search",
  "tracking",
  "bot-filters",
  "leads",
  "coupons",
  "favorites",
  "penny-items-pro",
  "penny-items-beta",
]);

/**
 * Public feed pages a signed-in free account sees exactly like a guest: "/", "/stores",
 * "/walmart", "/walmart/deals", "/walmart/toys", "/walmart/item/<key>".
 */
export function isFreePublicFeedPath(pathname: string): boolean {
  if (!isHomeFeedPath(pathname)) return false;
  const segments = pathname.split("/").filter(Boolean);
  return !(segments.length === 2 && MEMBER_STORE_SECTIONS.has(segments[1]));
}

/** "/walmart" for "/walmart/leads", "/home-depot" for "/home_depot/products", else "/". */
export function publicPathForMemberPath(pathname: string): string {
  const first = pathname.split("/").filter(Boolean)[0] || "";
  const retailer = PUBLIC_DEALS_RETAILERS.find((r) => r.slug === first || r.apiRetailer === first);
  return retailer ? `/${retailer.slug}` : "/";
}

/** Account pages free accounts keep, shown in the slim account shell (not the members app). */
const FREE_ACCOUNT_SHELL_PREFIXES = [
  "/settings",
  "/delete-account",
  "/change-password",
  "/forgot-password",
  "/reset-password",
  "/help",
  "/support",
  "/contact-us",
  "/privacy",
  "/terms",
  "/install",
  "/blog",
  "/guides",
  "/features/",
  "/deals",
  "/community-successes",
];

export function isFreeAccountShellPath(pathname: string): boolean {
  return FREE_ACCOUNT_SHELL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`),
  );
}
